import { Show, Suspense, Match, Switch, createSignal, For } from 'solid-js';
import {
	Button,
	Link,
	Picture,
	Container,
	DialogTrigger,
	Dialog,
	Heading,
	DialogContent,
	TextLink,
	Carousel,
	CarouselItem,
	Grid,
	GridThird,
	Section,
	GridTwoThirds,
} from '@troon/ui';
import { Meta, Title } from '@solidjs/meta';
import { createAsync, useAction } from '@solidjs/router';
import { IconPhone } from '@troon/icons/phone';
import { IconStarFilled } from '@troon/icons/star-filled';
import { IconStar } from '@troon/icons/star';
import { useFacility } from '../../../providers/facility';
import { FacilityBrand, gql, mutationAction, useMutation } from '../../../graphql';
import { getFacilitySchema } from '../../../modules/schema/facility';
import { Hero } from '../../../components/hero/photo';
import { courseTypeString } from '../../../components/facility/type-strings';
import { FacilityLocation } from '../../../components/facility/location';
import { cachedQuery } from '../../../graphql/cached-get';
import { SupportBadge } from '../../../components/support-badge';
import type { RouteDefinition, RouteSectionProps } from '@solidjs/router';
import type { Address, Course } from '../../../graphql';

export default function Course(props: RouteSectionProps) {
	const facility = useFacility();
	const otherInformation = createAsync(() => getExternalInfo({ slug: props.params.facilityId! }), {
		deferStream: true,
	});
	const handleFavoriteAction = useAction(useMutation(handleFavorite));
	const [openNonBookableDialog, setOpenNonBookableDialog] = createSignal(false);

	return (
		<>
			<Title>{`${facility.name} | Golf with Access | Book tee times`}</Title>
			<Meta
				name="description"
				content={`${facility.description?.slice(0, 154) ?? 'Book tee times at your favorite courses with Access'}…`}
			/>
			<script type="application/ld+json" innerText={getFacilitySchema(facility)} />

			<Suspense fallback={<div>loading…</div>}>
				<Hero src={facility.hero} crop="entropy" />
				<Container>
					<Show when={facility.logo}>
						{(logo) => (
							<div class="relative -top-32 z-20 -mb-32 aspect-square size-36 rounded-lg border border-neutral bg-white p-4 shadow">
								<Picture
									preload
									src={logo()}
									alt=""
									mode="contain"
									width={200}
									height={200}
									sizes="9rem"
									class="w-full"
								/>
							</div>
						)}
					</Show>
					<header class="mb-6 flex flex-col items-stretch gap-6 border-b border-neutral-500 pb-6 md:mb-8 md:flex-nowrap md:pb-8">
						<div class="flex flex-wrap items-center justify-start gap-4 lg:gap-8">
							<div class="flex shrink grow flex-col gap-2 md:gap-4">
								<Heading as="h1">{facility.name}</Heading>
								<p class="order-first text-sm tracking-widest text-neutral-800 uppercase">
									<span class="sr-only">Course type:</span> {courseTypeString[facility.type]}
									<Show when={facility.brand === FacilityBrand.TroonPartner}> • Troon Partner Course</Show>
								</p>
								<Show when={facility.address as Address}>
									{(address) => (
										<address class="text-sm text-neutral-800 not-italic" aria-label={`Address for ${facility.name}`}>
											{[address().street, address().city, [address().state, address().postalCode].join(' ')]
												.filter(Boolean)
												.join(', ')}
										</address>
									)}
								</Show>
								<SupportBadge points={facility.supportsAccessPoints} rates={facility.supportsTroonAccess} />
							</div>

							<div class="flex grow basis-full flex-row justify-end gap-4 md:grow-0 md:basis-auto">
								<div class="grow lg:grow-0">
									<Switch>
										<Match when={facility.isBookable}>
											<Button as={Link} href={`/course/${facility.slug!}/reserve-tee-time`} class="shrink text-nowrap">
												Book a tee time
											</Button>
										</Match>
										<Match when={facility.supportsTroonAccessPrive}>
											<Button
												as={Link}
												href={`/membership/request-tee-time?facility=${facility.slug}`}
												class="shrink text-nowrap"
											>
												Request a tee time
											</Button>
										</Match>
										<Match when={otherInformation()?.facilityRedirect.redirectUrl}>
											{(url) => (
												<Button as={Link} href={url()} class="shrink text-nowrap">
													Book a tee time
												</Button>
											)}
										</Match>
										<Match when={['DAILY_FEE_RESORT', 'SEMI_PRIVATE'].includes(facility.type)}>
											<Dialog
												key="non-bookable-facility"
												open={openNonBookableDialog()}
												onOpenChange={setOpenNonBookableDialog}
											>
												<DialogTrigger appearance="primary" class="w-full text-nowrap">
													Book a tee time
												</DialogTrigger>
												<DialogContent header="Book a Tee Time" headerLevel="h2">
													<div class="flex flex-col gap-4">
														<Picture
															src={facility.hero}
															width={520}
															height={153}
															sizes="32rem"
															alt=""
															class="overflow-hidden rounded-lg"
														/>
														<div class="flex flex-col gap-1">
															<p class="text-xl font-semibold">{facility.name}</p>
															<p class="order-first text-sm text-neutral-800 uppercase">
																<span class="sr-only">Course type:</span> {courseTypeString[facility.type]}
															</p>
															<Show when={facility.address as Address}>
																{(address) => (
																	<address
																		class="text-sm text-neutral-800 not-italic"
																		aria-label={`Address for ${facility.name}`}
																	>
																		{[
																			address().street,
																			address().city,
																			[address().state, address().postalCode].filter(Boolean).join(' '),
																		]
																			.filter(Boolean)
																			.join(', ')}
																	</address>
																)}
															</Show>
														</div>

														<p class="rounded-lg bg-neutral-100 p-4">
															This course is currently unavailable to book online. To book a tee time please call the
															number below:
														</p>

														<Show when={facility.phone}>
															{(phone) => (
																<div class="relative flex justify-between rounded-lg border border-neutral px-4 py-6">
																	<div class="flex flex-col">
																		<strong>Phone</strong>
																		<TextLink href={`tel:${phone()}`} class="after:absolute after:inset-0">
																			{phone()}
																		</TextLink>
																	</div>
																	<span class="flex size-10 items-center justify-center rounded-lg bg-brand-100 p-1 text-xl text-brand">
																		<IconPhone />
																	</span>
																</div>
															)}
														</Show>
													</div>
												</DialogContent>
											</Dialog>
										</Match>
									</Switch>
								</div>
								<div class="shrink">
									<Button
										appearance="secondary"
										onClick={() => {
											const data = new FormData();
											data.set('facilityId', facility.id ?? '');
											data.set('isFavorite', facility.isFavorite ? 'false' : 'true');
											handleFavoriteAction(data);
										}}
									>
										<Switch>
											<Match when={facility.isFavorite}>
												<IconStarFilled class="text-xl" />
											</Match>
											<Match when>
												<IconStar class="text-xl" />
											</Match>
										</Switch>
										<span class="sr-only">Favorite</span>
									</Button>
								</div>
							</div>
						</div>
					</header>

					<Grid>
						<GridTwoThirds class="flex flex-col gap-8">
							<section class="flex flex-col gap-4">
								<h2 class="text-xl font-semibold md:text-2xl">Overview</h2>
								<p class="whitespace-pre-line" innerHTML={facility.description ?? ''} />
							</section>

							<Show when={facility.gallery.length}>
								<section class="flex flex-col gap-4">
									<h2 class="text-xl font-semibold md:text-2xl">Course photos</h2>
									<Carousel auto="play" description={`Photos of ${facility.name}`} outerClass="rounded-lg">
										<For each={facility.gallery}>
											{(item) => (
												<CarouselItem class="mx-2 w-full overflow-hidden rounded-lg">
													<Picture
														src={item}
														width={600}
														height={300}
														sizes="(max-width: 768px) 95vw, 50vw"
														class="w-full"
														loading="lazy"
														alt={`Landscape putting greens and fairway photo of the ${facility.name} golf course`}
													/>
												</CarouselItem>
											)}
										</For>
									</Carousel>
								</section>
							</Show>
						</GridTwoThirds>

						<GridThird>
							<Section>
								<FacilityLocation
									courses={facility.courses.map((c) => c.name)}
									name={facility.name}
									slug={facility.slug}
									address={facility.address}
									phone={facility.phone}
									website={facility.website}
								/>
							</Section>
						</GridThird>
					</Grid>
				</Container>
			</Suspense>
		</>
	);
}

const getExternalInfoQuery = gql(`
query externalInfo($slug: String!) {
	facilityRedirect(facilitySlug: $slug) {
		redirectUrl
	}
}`);

const getExternalInfo = cachedQuery(getExternalInfoQuery, { onError: () => null });

const toggleFavorite = gql(`
mutation toggleFavoriteFacility($facilityId: String!, $isFavorite: Boolean!) {
	toggleFavoriteFacility(facilityId: $facilityId, isFavorite: $isFavorite) {
		name
		isFavorite
	}
}
`);

const handleFavorite = mutationAction(toggleFavorite, {
	transform: (data) => ({
		facilityId: data.get('facilityId') as string,
		isFavorite: data.get('isFavorite') === 'true',
	}),
	toast: (data) =>
		data?.toggleFavoriteFacility.isFavorite
			? `Added ${data?.toggleFavoriteFacility.name} as a favorite`
			: `Removed ${data?.toggleFavoriteFacility.name} as a favorite.`,
	revalidates: ['/v0/facilities/{idOrSlug}'],
});

export const route = { info: { nav: { hero: true } } } satisfies RouteDefinition;
